import React, { useRef } from 'react';
import { FundItem } from '../../models/Fund';
import { Box, Divider, ITheme, IconButton, Link, Menu, MenuItem, SvgIcon, Typography, useTheme } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { Utilities } from '../../utility/utils';
import LeoTag from '../common/LeoTag';
import StageDistributionGraph from '../common/StageDistributionGraph';
import GeographicDistributionGraph from '../common/GeographicDistributionGraph';
import { LeoImagesFundsPath } from '../../resources/api-constants';
import { useTranslation } from 'react-i18next';
import FavoriteFundButton from './FavoriteFundButton';
import { useAppDispatch, useAppSelector } from '../../store/reducers/store';
import { setSelectedFund, toggleShowMore } from '../../store/actions/libraryOfFunds';
import { useNavigate } from 'react-router-dom';
import { AUTHENTICATED_ROUTES } from '../../resources/routes-constants';
import { ReactComponent as sliderIcon } from '../../assets/sliders.svg';
import { ReactComponent as camemberIcon } from '../../assets/camembert.svg';
import { ReactComponent as worldIcon } from '../../assets/world.svg';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { addFundToPortfolio, removeFundToPortfolio, setDraggingStatus } from '../../store/actions/createPortfolio';
import { ReactComponent as addIcon } from '../../assets/add.svg';
import { ReactComponent as removeIcon } from '../../assets/remove.svg';
import useOnScreen from '../../hooks/useOnScreen';

export interface IListOfFundsItemProps {
    fund: FundItem;
    createPortfolio?: boolean;
    expand?: boolean
}

const ListOfFundsItem: React.FC<IListOfFundsItemProps> = ({ fund, createPortfolio, expand }) => {
    const theme = useTheme() as ITheme;
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const selectedFundId = useAppSelector(state => state.libraryOfFundsReducer.selectedFund);
    const addedFunds = useAppSelector(state => state.createPortfolio.portfolio.funds);
    const [menuAnchor, setMenuAnchor] = React.useState<null | HTMLElement>(null);
    const itemRef = useRef<HTMLDivElement>(null);
    const isVisible = useOnScreen(itemRef);

    const sectors = [...fund.sectorDistribution];
    sectors.sort((a, b) => b.proportion - a.proportion);

    const onSelectFund = (): void => {
        if (createPortfolio) {
            dispatch(setSelectedFund(fund.id));
            dispatch(toggleShowMore());
        } else {
            dispatch(setSelectedFund(fund.id));
        }
    }
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        if (createPortfolio) {
            if (addedFunds && addedFunds.findIndex(f => f.id === fund.id) !== -1) {
                dispatch(removeFundToPortfolio(fund.id));
            } else {
                dispatch(addFundToPortfolio(fund.id));
            }
        } else {
            setMenuAnchor(event.currentTarget);
        }
    };
    const handleClose = (event: React.MouseEvent<any>) => {
        event.stopPropagation();
        setMenuAnchor(null);
    };

    const addToNew = (event: React.MouseEvent<any>) => {
        event.stopPropagation();
        navigate(AUTHENTICATED_ROUTES.PORTFOLIO_CREATE_WITH_FUND_ROUTE.replace(":fundid", `${fund.id}`));
    }
    const addToExisting = (event: React.MouseEvent<any>) => {
        event.stopPropagation();
        navigate(AUTHENTICATED_ROUTES.PORTFOLIO_ADD_TO_ROUTE.replace(":id", `${fund.id}`));
    }
    const open = Boolean(menuAnchor);

    const isNotSelected = selectedFundId !== undefined && selectedFundId !== fund.id;
    const isSelected = selectedFundId !== undefined && selectedFundId === fund.id || expand;

    const onMoreClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        e.preventDefault();
        if (createPortfolio) {
            e.stopPropagation();
            dispatch(setSelectedFund(fund.id));
            dispatch(toggleShowMore());
        }
    }
    const addedToPortfolio = createPortfolio && addedFunds && addedFunds.findIndex(f => f.id === fund.id) !== -1;

    const onDragStart = (event: React.DragEvent<HTMLDivElement>): void => {
        dispatch(setDraggingStatus(true));
        event.dataTransfer.setData("Id", `${fund.id}`);
    }
    const onDragEnd = () => {
        dispatch(setDraggingStatus(false));
    }

    return <Box ref={itemRef} display={"flex"} flexDirection={"column"} flexShrink={0} width={"320px"} alignSelf={createPortfolio ? "flex-start" : undefined} height={"540px"}>{/*createPortfolio ? "100%" : undefined*/}
        {isVisible && <Box className={`listOfFundsItem ${!createPortfolio && isNotSelected ? "isNotSelected" : ""}`} bgcolor={theme.palette.background.paper} border={addedToPortfolio ? `1px solid ${theme.palette.primary.dark}` : `1px solid transparent`}
            width={"100%"} borderRadius={"8px"} flexGrow={1} flexDirection={"column"} display={"flex"} padding={"11px 18px 0px 18px"} boxShadow={"0px 2px 16px 0px #0000000F"} onClick={onSelectFund}
            draggable={createPortfolio} onDragStart={onDragStart} onDragEnd={onDragEnd}>
            <Box height={"22px"} marginTop={"-22px"} display={"flex"} gap={"4px"}>
                {fund.eltif && <Box height={"22px"} width={"67px"} bgcolor={theme.palette.primary.dark} alignSelf={"self-start"} borderRadius={"4px"} display={"flex"} justifyContent={"center"} alignItems={"center"}>
                    <Typography color={theme.palette.common.white} fontSize={"11px"} lineHeight={"20px"} fontWeight={"600"}>{t('LibraryOfFundsELTIF')}</Typography>
                </Box>}
                {fund.publicationDate.getTime() > Date.now() - 90*24*3600*1000 && <Box height={"22px"} bgcolor={theme.palette.leo.color11} alignSelf={"self-start"} borderRadius={"4px"} display={"flex"} justifyContent={"center"} alignItems={"center"} padding={"0 10px"}>
                    <Typography color={theme.palette.common.white} fontSize={"11px"} lineHeight={"20px"} fontWeight={"600"}>{t('LibraryOfFundFilterNew')}</Typography>
                </Box>}
                {fund.closingDate.getTime() < Date.now() + 90*24*3600*1000 && <Box height={"22px"} bgcolor={theme.palette.leo.color2} alignSelf={"self-start"} borderRadius={"4px"} display={"flex"} justifyContent={"center"} alignItems={"center"} padding={"0 10px"}>
                    <Typography color={theme.palette.common.white} fontSize={"11px"} lineHeight={"20px"} fontWeight={"600"}>{(Math.max(fund.closingDate.getTime() - Date.now(), 0) /(1000*3600*24)).toFixed(0)} {t('DashboardClosingSoonDaysLeft')}</Typography>
                </Box>}
            </Box>
            <Box display={"flex"} gap={"5px"} justifyContent={"space-between"} alignItems={"center"}>
                <img src={`${LeoImagesFundsPath}/${fund.logo}`} style={{ maxWidth: "150px", maxHeight: "100%", }} />
                <Box display={"flex"} gap={"8px"} alignItems={"center"}>
                    <FavoriteFundButton fundId={fund.id} />
                    <IconButton size='small' onClick={handleClick} sx={{ padding: 0 }}>
                        {addedToPortfolio ?
                            <SvgIcon component={removeIcon} inheritViewBox sx={{ color: theme.palette.primary.dark, width: "16px", height: "16px" }} />
                            // <RemoveIcon fontSize='small' sx={{ color: theme.palette.text.primary }} />
                            :
                            <SvgIcon component={addIcon} inheritViewBox sx={{ color: theme.palette.primary.dark, width: "16px", height: "16px" }} />
                            // <AddIcon fontSize='small' sx={{ color: theme.palette.text.primary }} />
                        }
                    </IconButton>
                    <Menu
                        id="add-menu"
                        anchorEl={menuAnchor}
                        open={open}
                        onClose={handleClose}
                        MenuListProps={{
                            'aria-labelledby': 'basic-button',
                        }}
                    >
                        <MenuItem onClick={addToNew}>{t('LibraryOfFundAddToNewPortfolio')}</MenuItem>
                        <Divider />
                        <MenuItem onClick={addToExisting}>{t('LibraryOfFundAddToExistingPortfolio')}</MenuItem>
                    </Menu>
                </Box>
            </Box>
            <Box display={"flex"} flexDirection={"column"} gap={"19px"} height={"100%"}>
                <Box display={"flex"} justifyContent={"space-between"} alignItems={"center"}>
                    <Box display={"flex"} flexDirection={"column"}>
                        <Typography fontSize={"14px"} fontWeight={"700"} lineHeight={"17.07px"} color={theme.palette.text.primary}>{fund.name}</Typography>
                    </Box>
                    <Box display={"flex"} flexDirection={"column"} justifyContent={"space-between"} alignItems={"center"}>
                        <Typography fontSize={"20px"} fontWeight={"600"} lineHeight={"24.38px"} color={theme.palette.text.primary}>€{Utilities.toFriendlyMoneyValue(fund.fundSize)}</Typography>
                        <Typography fontSize={"10px"} fontWeight={"600"} lineHeight={"10px"} color={theme.palette.text.secondary}>{t('LibraryOfFundsFundSize')}</Typography>
                    </Box>
                </Box>

                {createPortfolio && !isSelected ? <Box display={"flex"} justifyContent={"center"} width={"100%"}>
                    {!isSelected && <KeyboardArrowDownIcon sx={{ color: theme.palette.primary.dark }} />}
                </Box>
                    : <>
                        <Divider sx={{ margin: "0 -18px" }} />
                        <Box display={"flex"} flexDirection={"column"} gap={"19px"} justifyContent={"space-between"} flexGrow={1}>
                            <Box display={"flex"} alignItems={"center"} gap={"9px"}>
                                <SvgIcon component={sliderIcon} inheritViewBox sx={{ width: "16px", height: "16px" }} />
                                <Typography fontWeight={"500"} fontSize={"13px"} lineHeight={"15.85px"}>{t('LibraryOfFundsOverviewTabSectorTitle')}</Typography>
                            </Box>
                            <Box display={"flex"} flexWrap={"wrap"} gap={"8px 9px"}>
                                {sectors.slice(0, Math.min(3, fund.sectorDistribution.length)).map((s) =>
                                    <LeoTag key={`sector-${s.sector}`} text={s.sector} value={s.proportion} icon={s.icon} />)}
                            </Box>
                            <Divider />
                            <Box display={"flex"} alignItems={"center"} gap={"9px"}>
                                <SvgIcon component={camemberIcon} inheritViewBox sx={{ width: "16px", height: "16px" }} />
                                <Typography fontWeight={"500"} fontSize={"13px"} lineHeight={"15.85px"}>{t('LibraryOfFundsOverviewTabStageTitle')}</Typography>
                            </Box>
                            <StageDistributionGraph data={fund.stageDistribution} />
                            <Divider />
                            <Box display={"flex"} alignItems={"center"} gap={"9px"}>
                                <SvgIcon component={worldIcon} inheritViewBox sx={{ width: "16px", height: "16px" }} />
                                <Typography fontWeight={"500"} fontSize={"13px"} lineHeight={"15.85px"}>{t('LibraryOfFundsOverviewTabGeographicTitle')}</Typography>
                            </Box>
                            <GeographicDistributionGraph distribution={fund.geographicDistribution} />
                        </Box>
                        <Box display={"flex"} flexDirection={"column"} width={"100%"} marginTop={"auto"} marginBottom={"10px"}>
                            {/* <Box display={"flex"} flexGrow={1} justifyContent={"center"}>
                                {!isSelected && <KeyboardArrowDownIcon sx={{ color: theme.palette.primary.dark }} />}
                                {createPortfolio && isSelected && <KeyboardArrowUpIcon sx={{ color: theme.palette.primary.dark }} />}
                            </Box> */}
                            <Box display={"flex"} flexGrow={1} justifyContent={"flex-end"} justifySelf={"flex-end"} marginTop={!isSelected ? "-13px" : undefined}>
                                <Link href='#' onClick={onMoreClick} sx={{ color: theme.palette.primary.dark }}>{t('LibraryOfFundItemSeeMore')}</Link>
                            </Box>
                        </Box>
                    </>}
            </Box>
        </Box>}
        {!createPortfolio && <Box className={`listOfFundsItemArrow ${isSelected ? "isSelected" : undefined}`} display={"flex"} justifyContent={"center"}>
            <svg width="109" height="38" viewBox="0 0 109 38" fill="none" className='comingDown'>
                <path d="M0 0 H109 L 55 30Z" stroke="none" fill="white" className="drop-shadow" />
            </svg>
        </Box>}
    </Box>
}

export default ListOfFundsItem;